// Port of mobile src/services/firestoreNotes.ts to the Firebase JS SDK.
// The Firestore contract (paths, field names, allowed writes) must stay
// byte-identical with the mobile client — see firestore.rules.
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  writeBatch,
  query,
  orderBy,
  serverTimestamp,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type UpdateData,
} from 'firebase/firestore';
import {db} from '../firebase';
import type {Note, ChatMessage, UserProfile} from '../types/Note';

const notesCollection = (uid: string) => collection(db, `users/${uid}/notes`);

export const noteDocRef = (uid: string, noteId: string) =>
  doc(db, `users/${uid}/notes/${noteId}`);

export const noteFromDoc = (
  snap: QueryDocumentSnapshot | DocumentSnapshot,
): Note => {
  const data = snap.data() ?? {};
  return {
    id: snap.id,
    title: data.title ?? null,
    content: data.content ?? null,
    summarize: data.summarize ?? null,
    createdAt: data.createdAt ?? new Date().toString(),
    duration: data.duration ?? 0,
    videoId: data.videoId ?? null,
    emoji: data.emoji ?? null,
    language: data.language ?? null,
    status: data.status ?? 'ready',
    fileType: data.fileType ?? null,
    storagePath: data.storagePath ?? null,
    audioUrl: data.audioUrl ?? null,
    fileSize: data.fileSize ?? null,
    error: data.error ?? null,
    shared: data.shared ?? false,
    shareId: data.shareId ?? null,
  };
};

// Strips client-only fields (id), server-owned share fields, and undefined
// values.
const noteToDoc = (note: Note): Record<string, unknown> => {
  const {id: _id, shared: _shared, shareId: _shareId, ...rest} = note;
  const docData: Record<string, unknown> = {};
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined) {
      docData[key] = value;
    }
  });
  docData.updatedAt = serverTimestamp();
  return docData;
};

export const subscribeNotes = (
  uid: string,
  onNotes: (notes: Note[]) => void,
  onError?: (error: Error) => void,
): (() => void) =>
  onSnapshot(
    notesCollection(uid),
    snapshot => onNotes(snapshot.docs.map(noteFromDoc)),
    error => onError?.(error),
  );

export const subscribeNote = (
  uid: string,
  noteId: string,
  onNote: (note: Note | null) => void,
): (() => void) =>
  onSnapshot(noteDocRef(uid, noteId), snap =>
    onNote(snap.exists() ? noteFromDoc(snap) : null),
  );

export const createNoteDoc = async (uid: string, note: Note): Promise<void> => {
  await setDoc(noteDocRef(uid, note.id), noteToDoc(note));
};

// firestore.rules only allows clients to touch these note fields.
const EDITABLE_FIELDS = new Set([
  'title',
  'content',
  'summarize',
  'emoji',
  'language',
  'duration',
]);

export const updateNoteFields = async (
  uid: string,
  id: string,
  fields: Partial<Note>,
): Promise<void> => {
  const update: Record<string, unknown> = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (EDITABLE_FIELDS.has(key) && value !== undefined) {
      update[key] = value;
    }
  });
  update.updatedAt = serverTimestamp();
  await updateDoc(noteDocRef(uid, id), update as UpdateData<unknown>);
};

// Deleting the doc triggers the onNoteDeleted function, which cleans up
// Storage files and subcollections server-side.
export const deleteNoteDoc = async (
  uid: string,
  id: string,
): Promise<void> => {
  await deleteDoc(noteDocRef(uid, id));
};

export const upsertUserProfile = async (
  uid: string,
  fields: Record<string, unknown>,
): Promise<void> => {
  // Server-owned fields (entitlement, notesCreated, aiBudget,
  // voiceMessagesUsed) must never appear here — rules reject the write.
  await setDoc(
    doc(db, `users/${uid}`),
    {...fields, updatedAt: serverTimestamp()},
    {merge: true},
  );
};

export const subscribeUserProfile = (
  uid: string,
  onProfile: (profile: UserProfile) => void,
): (() => void) =>
  onSnapshot(
    doc(db, `users/${uid}`),
    snap => {
      const data = snap.data() ?? {};
      onProfile({
        entitlement: data.entitlement === 'pro' ? 'pro' : 'free',
        notesCreated: data.notesCreated ?? 0,
        voiceMessagesUsed: data.voiceMessagesUsed ?? 0,
      });
    },
    () => onProfile({entitlement: 'free'}),
  );

const messagesPath = (uid: string, noteId: string | null) =>
  noteId
    ? `users/${uid}/notes/${noteId}/messages`
    : `users/${uid}/assistantSessions/general/messages`;

export const subscribeNoteMessages = (
  uid: string,
  noteId: string | null,
  onMessages: (messages: ChatMessage[]) => void,
): (() => void) =>
  onSnapshot(
    query(collection(db, messagesPath(uid, noteId)), orderBy('createdAt', 'asc')),
    snapshot =>
      onMessages(
        snapshot.docs.map(d => ({
          id: d.id,
          role: d.data().role,
          content: d.data().content,
        })),
      ),
  );

/** Deletes every message in the assistant chat (per-note or general). */
export const clearAssistantMessages = async (
  uid: string,
  noteId: string | null,
): Promise<void> => {
  const snapshot = await getDocs(collection(db, messagesPath(uid, noteId)));
  const batchSize = 400;
  for (let i = 0; i < snapshot.docs.length; i += batchSize) {
    const batch = writeBatch(db);
    snapshot.docs.slice(i, i + batchSize).forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
};

export const noteDocExists = async (
  uid: string,
  id: string,
): Promise<boolean> => (await getDoc(noteDocRef(uid, id))).exists();
